import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconOutput } from '@components/icons/output';
import { RadioGroup } from '@components/radio-group';
import { Separator } from '@components/separator';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { minutes } from '@utils/milliseconds';

import {
  useCreateSmtpDomain,
  useInvalidateEmailDomains,
  useUpdateSmtpDomain,
  useVerifyEmailSettings,
} from 'api/company/hooks';
import {
  SMTP_AUTH_TYPE_BASIC,
  SMTP_AUTH_TYPE_OAUTH2,
  SmtpAuthType,
  UpdateSmtpDomainData,
} from 'api/company/requests';

import { buildAuthorizationUrl, deriveAuthorizationUrl } from './oauth2-url-utils';

type UnmanagedFormProps = {
  shortId?: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpLogin?: string;
  smtpAuthType?: SmtpAuthType;
  isVerified?: boolean;
};

export const useUnmanagedEmailDomainFormSidebar = () =>
  useSidebar(UnmanagedEmailDomainForm, { variant: 'REGULAR' });

type FormValues = {
  smtpHost: string;
  smtpPort: string;
  smtpLogin: string;
  smtpPassword: string;
  oauth2ClientId: string;
  oauth2ClientSecret: string;
  oauth2TokenUrl: string;
  oauth2Scope: string;
};

const Form = createForm<FormValues>();

const OAUTH2_REDIRECT_PATH = '/settings/email-domain';
const OAUTH2_POPUP_TIMEOUT_MS = minutes(5);
const OAUTH2_POPUP_POLL_INTERVAL_MS = 500;

const UnmanagedEmailDomainForm = ({
  shortId,
  smtpHost,
  smtpPort,
  smtpLogin,
  smtpAuthType: initialAuthType = SMTP_AUTH_TYPE_BASIC,
  isVerified: isCurrentlyVerified,
}: UnmanagedFormProps) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateSmtpDomain();
  const updateMutation = useUpdateSmtpDomain();
  const verifyMutation = useVerifyEmailSettings();
  const { invalidate } = useInvalidateEmailDomains();
  const isEditing = Boolean(shortId);
  const formStore = useCreateFormStore();
  const values = formStore.useFormValues();
  const [isVerified, setIsVerified] = useState<boolean | undefined>(isCurrentlyVerified);
  const [authType, setAuthType] = useState<SmtpAuthType>(initialAuthType);
  const [authorizationCode, setAuthorizationCode] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isOAuth2 = authType === SMTP_AUTH_TYPE_OAUTH2;

  // Clean up popup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  const submit = async (formValues: FormValues) => {
    try {
      if (isEditing && shortId) {
        const data: UpdateSmtpDomainData = {
          smtpHost: formValues.smtpHost,
          smtpPort: parseInt(formValues.smtpPort, 10),
          smtpLogin: formValues.smtpLogin,
        };

        if (isOAuth2) {
          data.oauth2ClientId = formValues.oauth2ClientId ?? undefined;
          data.oauth2ClientSecret = formValues.oauth2ClientSecret ?? undefined;
          data.oauth2TokenUrl = formValues.oauth2TokenUrl ?? undefined;
          data.oauth2Scope = formValues.oauth2Scope ?? undefined;
          if (authorizationCode) {
            data.oauth2AuthorizationCode = authorizationCode;
            data.oauth2RedirectUri = window.location.origin + OAUTH2_REDIRECT_PATH;
          }
        } else {
          data.smtpPassword = formValues.smtpPassword ?? undefined;
        }

        await updateMutation.mutateAsync({ shortId, data });
      } else {
        if (isOAuth2) {
          await createMutation.mutateAsync({
            smtpHost: formValues.smtpHost,
            smtpPort: parseInt(formValues.smtpPort, 10),
            smtpLogin: formValues.smtpLogin,
            smtpAuthType: SMTP_AUTH_TYPE_OAUTH2,
            oauth2ClientId: formValues.oauth2ClientId,
            oauth2ClientSecret: formValues.oauth2ClientSecret,
            oauth2TokenUrl: formValues.oauth2TokenUrl,
            oauth2Scope: formValues.oauth2Scope || undefined,
            oauth2AuthorizationCode: authorizationCode || undefined,
            oauth2RedirectUri: window.location.origin + OAUTH2_REDIRECT_PATH,
          });
        } else {
          await createMutation.mutateAsync({
            smtpHost: formValues.smtpHost,
            smtpPort: parseInt(formValues.smtpPort, 10),
            smtpLogin: formValues.smtpLogin,
            smtpPassword: formValues.smtpPassword,
          });
        }
      }
      await invalidate();
      hideSidebar();
    } catch {
      // Error is handled by react-query
    }
  };

  const handleTestRequest = async () => {
    const response = await verifyMutation.mutateAsync({
      smtpHost: values.smtpHost as string,
      smtpPort: parseInt(values.smtpPort as string, 10),
      smtpLogin: values.smtpLogin as string,
      smtpPassword: values.smtpPassword as string,
    });
    setIsVerified(response.isVerified);
  };

  const handleAuthorize = useCallback(() => {
    const clientId = values.oauth2ClientId as string;
    const tokenUrl = values.oauth2TokenUrl as string;
    const scope = (values.oauth2Scope as string) || '';

    if (!clientId || !tokenUrl) {
      return;
    }

    const authEndpoint = deriveAuthorizationUrl(tokenUrl);
    if (!authEndpoint) {
      return;
    }

    const redirectUri = window.location.origin + OAUTH2_REDIRECT_PATH;
    const authUrl = buildAuthorizationUrl(authEndpoint, clientId, scope, redirectUri);

    const popup = window.open(authUrl, 'oauth2_authorize', 'width=600,height=700');
    if (!popup) {
      showError(t('oauth2PopupBlocked'));
      return;
    }

    popupRef.current = popup;

    const pollStartTime = Date.now();

    // Poll the popup location to detect the redirect back to our domain
    pollTimerRef.current = setInterval(() => {
      try {
        // Timeout after 5 minutes
        if (Date.now() - pollStartTime > OAUTH2_POPUP_TIMEOUT_MS) {
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          if (!popup.closed) {
            popup.close();
          }
          showError(t('oauth2AuthorizationTimeout'));
          return;
        }

        if (popup.closed) {
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          return;
        }

        const popupUrl = popup.location.href;
        if (popupUrl.startsWith(window.location.origin)) {
          const url = new URL(popupUrl);
          const code = url.searchParams.get('code');
          const error = url.searchParams.get('error');
          const errorDescription = url.searchParams.get('error_description');

          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          popup.close();

          if (code) {
            setAuthorizationCode(code);
          } else if (error) {
            showError(errorDescription || error);
            Sentry.captureMessage('OAuth2 email domain authorization failed', {
              level: 'error',
              extra: { error, errorDescription },
            });
          }
        }
      } catch {
        // Cross-origin error while popup is on the provider's page — keep polling
      }
    }, OAUTH2_POPUP_POLL_INTERVAL_MS);
  }, [values.oauth2ClientId, values.oauth2TokenUrl, values.oauth2Scope, t]);

  const handleAuthTypeChange = (value: string) => {
    setAuthType(value as SmtpAuthType);
    setIsVerified(undefined);
    setAuthorizationCode(null);
  };

  const isDirty = formStore.useIsDirty();

  const isBasicFieldsFilled = Boolean(
    values.smtpHost && values.smtpPort && values.smtpLogin && values.smtpPassword
  );

  const isOAuth2Ready = Boolean(
    values.smtpHost &&
      values.smtpPort &&
      values.smtpLogin &&
      values.oauth2ClientId &&
      values.oauth2ClientSecret &&
      values.oauth2TokenUrl &&
      authorizationCode
  );

  const isOAuth2FieldsFilled = Boolean(
    values.oauth2ClientId && values.oauth2ClientSecret && values.oauth2TokenUrl
  );

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  const canSubmit = isOAuth2
    ? (isDirty || Boolean(authorizationCode)) && (isEditing || isOAuth2Ready)
    : isDirty;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEditing ? IconEdit : IconEmail}
        iconColor="elementBlue100"
        title={isEditing ? t('editEmailDomain') : t('addEmailDomain')}
        subtitle={t('subtitle')}
      />
      <Sidebar.Content>
        <Form onSubmit={submit} store={formStore}>
          <Stack space={6}>
            <Text color="textSecondary" font="regular14" text={t('description')} />

            {/* Auth type is only shown during creation — changing it after creation requires
                different credentials and would break existing email address associations. */}
            {!isEditing && (
              <RadioGroup
                defaultValue={authType}
                label={t('authTypeLabel')}
                onChange={handleAuthTypeChange}
              >
                <RadioGroup.Option label={t('authTypeBasic')} value={SMTP_AUTH_TYPE_BASIC} />
                <RadioGroup.Option label={t('authTypeOAuth2')} value={SMTP_AUTH_TYPE_OAUTH2} />
              </RadioGroup>
            )}

            <Stack space={4}>
              <Form.TextField
                id="smtpHost"
                label={t('emailSettingsSmtpHost')}
                validators={['required']}
                placeholder={t('smtpHostPlaceholder')}
                defaultValue={smtpHost}
                onChange={(value) =>
                  setIsVerified(value !== smtpHost ? undefined : isCurrentlyVerified)
                }
              />
              <Form.TextField
                id="smtpPort"
                label={t('emailSettingsSmtpPort')}
                validators={['required', 'number']}
                placeholder={t('smtpPortPlaceholder')}
                defaultValue={smtpPort ?? '587'}
                onChange={(value) =>
                  setIsVerified(value !== smtpPort ? undefined : isCurrentlyVerified)
                }
              />
              <Form.TextField
                id="smtpLogin"
                label={t('emailSettingsSmtpLogin')}
                validators={['required']}
                placeholder={t('smtpLoginPlaceholder')}
                defaultValue={smtpLogin}
                onChange={(value) =>
                  setIsVerified(value !== smtpLogin ? undefined : isCurrentlyVerified)
                }
              />

              {!isOAuth2 && (
                <Form.PasswordField
                  id="smtpPassword"
                  label={
                    isEditing
                      ? t('emailSettingsSmtpPasswordEditing')
                      : t('emailSettingsSmtpPassword')
                  }
                  validators={isEditing ? [] : ['required']}
                  placeholder={t('smtpPasswordPlaceholder')}
                  onChange={(value) =>
                    setIsVerified(value !== '' ? undefined : isCurrentlyVerified)
                  }
                />
              )}

              {isOAuth2 && (
                <>
                  <Separator />
                  <Text font="semibold14" text={t('oauth2CredentialsTitle')} />
                  <Form.TextField
                    id="oauth2ClientId"
                    label={isEditing ? t('oauth2ClientIdEditing') : t('oauth2ClientId')}
                    validators={isEditing ? [] : ['required']}
                    placeholder={
                      isEditing
                        ? t('oauth2FieldEditingPlaceholder')
                        : t('oauth2ClientIdPlaceholder')
                    }
                  />
                  <Form.PasswordField
                    id="oauth2ClientSecret"
                    label={isEditing ? t('oauth2ClientSecretEditing') : t('oauth2ClientSecret')}
                    validators={isEditing ? [] : ['required']}
                    placeholder={
                      isEditing
                        ? t('oauth2FieldEditingPlaceholder')
                        : t('oauth2ClientSecretPlaceholder')
                    }
                  />
                  <Form.TextField
                    id="oauth2TokenUrl"
                    label={isEditing ? t('oauth2TokenUrlEditing') : t('oauth2TokenUrl')}
                    validators={isEditing ? [] : ['required']}
                    placeholder={
                      isEditing
                        ? t('oauth2FieldEditingPlaceholder')
                        : t('oauth2TokenUrlPlaceholder')
                    }
                  />
                  <Form.TextField
                    id="oauth2Scope"
                    label={t('oauth2Scope')}
                    placeholder={
                      isEditing ? t('oauth2FieldEditingPlaceholder') : t('oauth2ScopePlaceholder')
                    }
                  />

                  <Card space={4}>
                    <Stack space={4}>
                      <div>
                        <Text font="semibold14" text={t('oauth2AuthorizeTitle')} />
                        <Text font="regular14" text={t('oauth2AuthorizeDescription')} />
                      </div>
                      <ButtonGroup align="left">
                        <Button
                          iconLeft={<IconOutput />}
                          text={authorizationCode ? t('oauth2Reauthorize') : t('oauth2Authorize')}
                          variant="primary-blue"
                          onClick={handleAuthorize}
                          isDisabled={!isOAuth2FieldsFilled}
                        />
                      </ButtonGroup>
                      <Stack orientation="horizontal" align="center" space={2}>
                        <AuthorizationStatus hasCode={Boolean(authorizationCode)} />
                      </Stack>
                    </Stack>
                  </Card>
                </>
              )}

              {/* OAuth2 skips the pre-save "Test credentials" step because the authorization
                  code is single-use — the server tests the SMTP connection during create/update. */}
              {!isOAuth2 && (
                <Card space={4}>
                  <Stack space={4}>
                    <div>
                      <Text font="semibold14" text={t('checkCredentials')} />
                      <Text font="regular14" text={t('checkCredentialsDescription')} />
                    </div>
                    {!isVerified && isDirty && (
                      <ButtonGroup align="left">
                        <Button
                          iconLeft={<IconOutput />}
                          text={t('testCredentials')}
                          variant="primary-blue"
                          onClick={handleTestRequest}
                          isDisabled={verifyMutation.isLoading || !isBasicFieldsFilled}
                        />
                      </ButtonGroup>
                    )}
                    <Stack orientation="horizontal" align="center" space={2}>
                      <VerifiedStatus isVerified={isVerified} />
                    </Stack>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Stack>
          <Sidebar.Footer>
            <Form.Actions>
              <Button
                text={t('core.cancel')}
                isDisabled={isLoading}
                onClick={hideSidebar}
                variant="secondary"
              />
              <Form.Submit
                text={isEditing ? t('core.save') : t('addDomain')}
                variant="primary-blue"
                isDisabled={isLoading || !canSubmit}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const VerifiedStatus = ({ isVerified }: { isVerified: boolean | undefined }) => {
  const { t } = useScopedTranslate(locale);
  switch (isVerified) {
    case true:
      return (
        <>
          <IconFormIndicatorSuccess />
          <Text color="green100" font="regular14" text={t('verifiedCredentials')} />
        </>
      );
    case false:
      return (
        <>
          <IconFormIndicatorError />
          <Text color="elementRed100" font="regular14" text={t('invalidCredentials')} />
        </>
      );
    case undefined:
    default:
      return (
        <>
          <IconFormIndicatorInfo color="indigo100" />
          <Text color="indigo100" font="regular14" text={t('notVerifiedCredentials')} />
        </>
      );
  }
};

const AuthorizationStatus = ({ hasCode }: { hasCode: boolean }) => {
  const { t } = useScopedTranslate(locale);
  if (hasCode) {
    return (
      <>
        <IconFormIndicatorSuccess />
        <Text color="green100" font="regular14" text={t('oauth2AuthorizationObtained')} />
      </>
    );
  }
  return (
    <>
      <IconFormIndicatorInfo color="indigo100" />
      <Text color="indigo100" font="regular14" text={t('oauth2AuthorizationPending')} />
    </>
  );
};

const locale = {
  en: {
    addEmailDomain: 'Add email domain',
    editEmailDomain: 'Edit email domain',
    subtitle: '(SMTP)',
    authTypeLabel: 'Authentication method',
    authTypeBasic: 'Username & password',
    authTypeOAuth2: 'OAuth2 (Microsoft 365, Google Workspace)',
    emailSettingsSmtpHost: 'Host',
    emailSettingsSmtpPort: 'Port',
    emailSettingsSmtpLogin: 'Login',
    emailSettingsSmtpPassword: 'Password',
    emailSettingsSmtpPasswordEditing: 'Password (leave blank to keep current password)',
    smtpHostPlaceholder: 'smtp.acme.com...',
    smtpPortPlaceholder: '587',
    smtpLoginPlaceholder: 'Usually an email address...',
    smtpPasswordPlaceholder: 'Password',
    checkCredentials: 'Check credentials',
    checkCredentialsDescription: 'Press the Test credentials button below to test your credentials',
    testCredentials: 'Test credentials',
    notVerifiedCredentials: 'Credentials not verified',
    verifiedCredentials: 'Credentials verified',
    invalidCredentials: 'Credentials invalid',
    addDomain: 'Add domain',
    description: 'Please refer to your email provider for the SMTP settings',
    oauth2CredentialsTitle: 'OAuth2 credentials',
    oauth2ClientId: 'Client ID',
    oauth2ClientIdEditing: 'Client ID (leave blank to keep current)',
    oauth2ClientIdPlaceholder: 'Application (client) ID from your OAuth2 app registration',
    oauth2ClientSecret: 'Client secret',
    oauth2ClientSecretEditing: 'Client secret (leave blank to keep current)',
    oauth2ClientSecretPlaceholder: 'Client secret value',
    oauth2TokenUrl: 'Token URL',
    oauth2TokenUrlEditing: 'Token URL (leave blank to keep current)',
    oauth2TokenUrlPlaceholder: 'https://login.microsoftonline.com/{tenant}/oauth2/v2.0/token',
    oauth2Scope: 'Scope (optional)',
    oauth2ScopePlaceholder: 'https://outlook.office365.com/.default',
    oauth2FieldEditingPlaceholder: 'Currently configured',
    oauth2AuthorizeTitle: 'Authorization',
    oauth2AuthorizeDescription:
      'Click the button below to authorize access to send emails via your provider',
    oauth2Authorize: 'Authorize',
    oauth2Reauthorize: 'Re-authorize',
    oauth2AuthorizationObtained: 'Authorization code obtained',
    oauth2AuthorizationPending: 'Authorization required',
    oauth2PopupBlocked: 'Please allow popups for this site to authorize OAuth2',
    oauth2AuthorizationTimeout: 'Authorization timed out. Please try again.',
  },
};
